import React, { useState } from 'react';
import { 
  ChartBarIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  PhoneIcon,
  ServerIcon,
  GlobeAltIcon,
  UserIcon,
  ShieldCheckIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface LogEntry {
  id: string;
  timestamp: string;
  type: string;
  message: string;
  status: string;
  details: string;
  sentBy?: string;
}

export const LiveLogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [smsFilterType, setSmsFilterType] = useState('all');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [systemFilterType, setSystemFilterType] = useState('health');
  const [exportFormat, setExportFormat] = useState('csv');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Mock data for demonstration
  const smsLogs: LogEntry[] = [
    {
      id: '1',
      timestamp: '2024-03-20 15:30:00',
      type: 'SMS',
      message: 'Message delivered successfully',
      status: 'success',
      details: 'To: +1234567890 via Vodafone',
      sentBy: 'John Doe'
    },
    // Add more mock SMS logs
  ];

  const systemLogs: LogEntry[] = [
    {
      id: '1',
      timestamp: '2024-03-20 15:29:00',
      type: 'System Health',
      message: 'CPU Usage spike detected',
      status: 'warning',
      details: 'CPU Usage: 85%'
    },
    // Add more mock system logs
  ];

  const platformUsers = [
    { id: 'user1', name: 'John Doe' },
    { id: 'user2', name: 'Jane Smith' },
    { id: 'user3', name: 'Mike Johnson' },
    { id: 'user4', name: 'Sarah Williams' }
  ];

  const handleExport = (section: 'sms' | 'system') => {
    // Export logic here
  };

  const handleUserSelect = (userId: string) => {
    if (!selectedUsers.includes(userId)) {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter(id => id !== userId));
  };

  const handleGenerateLogs = (section: 'sms' | 'system') => {
    const filters = {
      startDateTime: `${startDate} ${startTime}`,
      endDateTime: `${endDate} ${endTime}`,
      filterType: section === 'sms' ? smsFilterType : systemFilterType,
      network: selectedNetwork,
      country: selectedCountry,
      user: selectedUser,
      selectedUsers: selectedUsers
    };
    
    // Here you would make an API call to fetch filtered logs
    console.log(`Generating ${section} logs with filters:`, filters);
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <ChartBarIcon className="w-8 h-8 text-[#FF5722] mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Live Logs Monitor</h1>
        </div>

        {/* SMS Logs Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <PhoneIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">SMS Logs</h2>
            </div>
            <div className="flex items-center space-x-4">
              <select
                className="border rounded-md p-2 text-sm"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
              >
                <option value="csv">CSV</option>
                <option value="pdf">PDF</option>
              </select>
              <button 
                onClick={() => handleExport('sms')}
                className="flex items-center space-x-2 bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* SMS Filters */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <div className="md:col-span-2 flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter Type</label>
                <select
                  value={smsFilterType}
                  onChange={(e) => setSmsFilterType(e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="all">All Mobile Networks</option>
                  <option value="network">Network Specific</option>
                  <option value="country">Country Specific</option>
                  <option value="user">User Specific</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Users</label>
                <div className="relative">
                  <select
                    onChange={(e) => handleUserSelect(e.target.value)}
                    className="w-full border rounded-md p-2"
                    value=""
                  >
                    <option value="">Add User</option>
                    {platformUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedUsers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedUsers.map(userId => {
                      const user = platformUsers.find(u => u.id === userId);
                      return (
                        <span
                          key={userId}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-[#FF5722] text-white"
                        >
                          {user?.name}
                          <button
                            onClick={() => handleRemoveUser(userId)}
                            className="ml-1 hover:text-gray-200"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
              <div className="flex gap-2">
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full border rounded-md p-2"
                />
                <button
                  onClick={() => handleGenerateLogs('sms')}
                  className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] whitespace-nowrap flex items-center gap-2"
                >
                  Generate
                  {selectedUsers.length > 0 && (
                    <span className="bg-white text-[#FF5722] rounded-full px-2 py-0.5 text-sm">
                      {selectedUsers.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {smsFilterType === 'network' && (
              <div className="md:col-span-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Network</label>
                <select
                  value={selectedNetwork}
                  onChange={(e) => setSelectedNetwork(e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="">Select Network</option>
                  <option value="vodafone">Vodafone</option>
                  <option value="tmobile">T-Mobile</option>
                  <option value="att">AT&T</option>
                </select>
              </div>
            )}

            {smsFilterType === 'country' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="">Select Country</option>
                  <option value="us">United States</option>
                  <option value="uk">United Kingdom</option>
                  <option value="ca">Canada</option>
                </select>
              </div>
            )}

            {smsFilterType === 'user' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="">Select User</option>
                  <option value="user1">John Doe</option>
                  <option value="user2">Jane Smith</option>
                </select>
              </div>
            )}
          </div>

          {/* SMS Logs Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {smsLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.timestamp}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.sentBy}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{log.message}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{log.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* System Logs Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ServerIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">System Logs</h2>
            </div>
            <div className="flex items-center space-x-4">
              <select
                className="border rounded-md p-2 text-sm"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
              >
                <option value="csv">CSV</option>
                <option value="pdf">PDF</option>
              </select>
              <button 
                onClick={() => handleExport('system')}
                className="flex items-center space-x-2 bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* System Logs Filters */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Log Type</label>
              <select
                value={systemFilterType}
                onChange={(e) => setSystemFilterType(e.target.value)}
                className="w-full border rounded-md p-2"
              >
                <option value="health">System Health Logs</option>
                <option value="security">Security Alert Logs</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
              <div className="flex gap-2">
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full border rounded-md p-2"
                />
                <button
                  onClick={() => handleGenerateLogs('system')}
                  className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] whitespace-nowrap"
                >
                  Generate
                </button>
              </div>
            </div>
          </div>

          {/* System Logs Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {systemLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.timestamp}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.type}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{log.message}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        log.status === 'warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{log.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveLogsPage; 